import { SlideHeader } from "../../../SlideHeader";
import { SupLink } from "../../../SlideContext";

export default function TheRaise() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8 md:[&_header]:mb-6">
      <SlideHeader eyebrow="The Raise">
        <h1 className="text-3xl font-semibold leading-tight tracking-tight md:text-6xl md:leading-[1.1]">
          24 months to €2.8M ARR, CF-positive
        </h1>
        <p className="mt-3 text-xl font-medium tracking-tight text-slate-700 md:mt-4 md:text-3xl">
          €1.3M SAFE at 12–15% dilution / €8.7–10.8M post-money
        </p>
      </SlideHeader>

      {/* Month 24 — the objective. One grouped object: statement, the four
          numbers behind it, and the downside line the raise is sized on. */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700 md:tracking-[0.2em]">
          Month 24 — 2.7× YoY growth at ~37% operating margin
        </p>
        <div className="mt-3 overflow-hidden rounded-2xl bg-blue-50 ring-1 ring-blue-200">
          <p className="px-6 pt-5 text-lg font-semibold leading-snug text-blue-900 md:px-8 md:pt-6 md:text-2xl">
            At month 24 the company is{" "}
            <span className="font-bold">
              cash-flow positive at ~€2.8M ARR and growing 2.7×
              year-over-year
            </span>{" "}
            — which makes a Series A{" "}
            <span className="font-bold">optional</span>: raise from strength
            for acceleration, or don't raise at all.
          </p>
          <div className="mt-4 grid grid-cols-2 gap-3 px-6 pb-5 md:mt-5 md:grid-cols-4 md:gap-4 md:px-8 md:pb-6">
            <Metric value="€2.8M" label="ARR run-rate" sub="base plan" />
            <Metric value="m17" label="revenue covers burn" sub="~€750K in bank at m24" />
            <Metric value="1,585 + 26" label="accounts at m24" sub="self-serve + enterprise" />
            <Metric value="7 FT" label="team at m24" sub="2 founders + 5 hires, + PT & BD" />
          </div>
          <p className="border-t border-blue-200/70 bg-white/60 px-6 py-3 text-sm leading-snug text-slate-700 md:px-8 md:text-base">
            <span className="font-semibold text-slate-900">
              Sized conservatively — a lean team, GTM-heavy, for 24 months:
            </span>{" "}
            even if growth comes in a third under plan, the company still
            lands cash-flow positive — no bridge round.
          </p>
        </div>
      </div>

      {/* Use of funds — the 24-month operating plan the raise unlocks */}
      <div className="mt-6">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700 md:tracking-[0.2em]">
          Use of funds — revenue funds ~82% of the spend
        </p>
        <p className="mt-2 text-sm text-slate-700 md:text-base">
          Over 24 months the company spends €3.0M and earns ~€2.4M — the raise
          starts the engine and insures the downside.
        </p>
        <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
          <Cat pct="72%" label="Team" amount="€2.14M" sub="salaries · contractors" />
          <Cat pct="22%" label="Marketing" amount="€652K" sub="ads · sponsorships · BD" />
          <Cat pct="3%" label="Product Infra" amount="€95K" sub="dev tooling · COGS" />
          <Cat pct="3%" label="G&A" amount="€90K" sub="travel · legal · desk tech" />
        </div>
      </div>

      {/* Founder leverage — the engineering org design behind the numbers */}
      <div className="mt-6">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700 md:tracking-[0.2em]">
          Founder × AI leverage — engineering at ~9% of spend, not 40%+
        </p>
        <p className="mt-2 text-sm leading-snug text-slate-700 md:text-base">
          Thomas (CEO) is also effectively the CTO and entire engineering team
          other than one TBH DevOps expert. AI agents author 100% of the
          platform's code and ship it to production nightly through the
          automated development &amp; testing system Thomas engineered and
          oversees.
          <SupLink to="ai-native-factory">F</SupLink>
        </p>
      </div>

    </div>
  );
}

function Metric({
  value,
  label,
  sub,
}: {
  value: string;
  label: string;
  sub: string;
}) {
  return (
    <div className="rounded-lg bg-white p-3 ring-1 ring-blue-200/70 md:p-4">
      <p className="text-xl font-bold tracking-tight text-slate-900 md:text-3xl">
        {value}
      </p>
      <p className="mt-0.5 text-xs font-medium text-slate-900 md:text-sm">
        {label}
      </p>
      <p className="text-xs text-slate-500 md:text-sm">{sub}</p>
    </div>
  );
}

function Cat({
  pct,
  label,
  amount,
  sub,
}: {
  pct: string;
  label: string;
  amount: string;
  sub?: string;
}) {
  return (
    <div className="rounded-lg bg-slate-50 p-3 ring-1 ring-slate-200 md:p-4">
      <p className="text-xl font-bold tracking-tight text-slate-900 md:text-2xl">
        {pct}
      </p>
      <p className="mt-0.5 text-xs font-medium text-slate-900 md:text-sm">
        {label}
        <span className="text-slate-500"> — {amount}</span>
      </p>
      {sub ? <p className="text-xs text-slate-500 md:text-sm">{sub}</p> : null}
    </div>
  );
}
